import React, { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, ShieldCheck, Copy, Clock, ArrowRight, Sparkles, Users } from "lucide-react";
import WinsMarquee from "@/components/WinsMarquee";
import RefundEstimator from "@/components/RefundEstimator";
import Footer from "@/components/Footer";

const HERO_IMG = "https://images.unsplash.com/photo-1512428559087-560fa5ceab42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxwZXJzb24lMjB1c2luZyUyMHNtYXJ0cGhvbmUlMjBhcHAlMjBjb25zdW1lciUyMHN1YnNjcmlwdGlvbnxlbnwwfHx8fDE3ODgyODQwMTB8MA&ixlib=rb-4.1.0&q=85";

function Step({ n, title, desc }) {
  return (
    <div className="flex items-start gap-4">
      <div className="shrink-0 w-9 h-9 rounded-full bg-[#EEF4FF] text-[#1E3A8A] grid place-items-center font-bold">{n}</div>
      <div>
        <h4 className="font-semibold text-slate-900">{title}</h4>
        <p className="text-sm text-slate-600 mt-1">{desc}</p>
      </div>
    </div>
  );
}

function initials(name) {
  const parts = (name || "").split(" ").filter(Boolean);
  return (parts[0]?.[0] || "F") + (parts[1]?.[0] || "");
}

export default function Landing() {
  const [wall, setWall] = useState(null);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/founders/wall").then((r) => setWall(r.data)).catch(()=>{});
    api.get("/stats/public").then((r) => setStats(r.data)).catch(()=>{});
  }, []);
  return (
    <div className="min-h-screen bg-[#FAFBFD]">
      <Nav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-in">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold mb-4" data-testid="founding-100-badge">
              <Sparkles className="w-3.5 h-3.5" /> First 100 signups: your first case is FREE
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EEF4FF] text-[#1E3A8A] text-xs font-medium mb-6 ml-2" data-testid="hero-badge">
              <Sparkles className="w-3.5 h-3.5" /> Concierge-drafted, one-tap copy
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.1]">
              Stop unwanted subscription charges <span className="brand-gradient-text">in their tracks.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl">
              Recover money from Netflix, gym apps, SaaS trials, and streaming services that kept billing after you cancelled. AI-drafted messages + a 4-step escalation playbook.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/auth" data-testid="hero-get-refund-button">
                <Button size="lg" className="w-full sm:w-auto rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white px-8 h-14 text-base font-semibold">
                  Get your subscription refund <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/playbooks" data-testid="hero-see-playbooks-link">
                <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full h-14 px-8 text-base">
                  See merchant playbooks
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-xs text-slate-500 max-w-md" data-testid="hero-disclaimer">
              $19 per case, non-refundable. Powerhouse Refunds is a template and guidance tool — refund outcomes are not guaranteed.
            </p>
            <div className="mt-10 flex items-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> No auto-submit</div>
              <div className="flex items-center gap-2"><Copy className="w-4 h-4 text-emerald-600" /> One-tap copy</div>
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-emerald-600" /> 4-step playbook</div>
            </div>
          </div>
          <div className="relative">
            <div className="trust-card overflow-hidden">
              <img src={HERO_IMG} alt="User reviewing subscription on phone" className="w-full h-[420px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 trust-card p-4 max-w-xs hidden sm:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">Refund confirmed</p>
                  <p className="text-xs text-slate-500">$47.98 · Peacock · 4 days</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live stats ticker */}
      {stats && (stats.resolved_case_count > 0 || stats.total_case_count > 0) && (
        <section className="border-t border-slate-200 bg-white" data-testid="stats-ticker">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-3xl sm:text-4xl font-bold brand-gradient-text" data-testid="stat-recovered">${Math.round(stats.total_recovered).toLocaleString()}</p>
              <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">Recovered</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-slate-900" data-testid="stat-resolved">{stats.resolved_case_count}</p>
              <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">Cases resolved</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-slate-900" data-testid="stat-cases">{stats.total_case_count}</p>
              <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">Cases opened</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-slate-900" data-testid="stat-users">{stats.total_users}</p>
              <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">People helped</p>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-2 pb-6">
            <WinsMarquee />
          </div>
        </section>
      )}

      {/* Refund estimator */}
      <section className="bg-[#FAFBFD] border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <RefundEstimator />
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight text-center">How it works</h2>
          <p className="text-slate-600 text-center mt-3 max-w-2xl mx-auto">A calm, concierge-style walkthrough — no legal jargon, no auto-submissions. You always paste and click send.</p>
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            <Step n="1" title="Tell us about the charge" desc="Merchant, amount, cancel date, charge date. Takes 90 seconds." />
            <Step n="2" title="Unlock your 4-message playbook" desc="One-time $19 fee. Message 1 through 4 unlock instantly, ready to copy." />
            <Step n="3" title="Copy, paste, click send" desc="One-tap Copy on each message. You paste into the merchant's support form or email." />
            <Step n="4" title="Escalate step-by-step" desc="Wait 48–72h. If ignored, Message 2 → Message 3 → Message 4. Progress bar tracks it all." />
          </div>
        </div>
      </section>

      {/* Merchants */}
      <section className="bg-[#FAFBFD]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Merchant playbooks</h2>
              <p className="text-slate-600 mt-2">Every merchant handles refunds differently. Our playbooks tell you exactly which channel to use.</p>
            </div>
            <Link to="/playbooks" data-testid="merchants-view-all-link">
              <Button variant="outline" className="rounded-full">View all</Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              {name:"Netflix", slug:"netflix"},
              {name:"Spotify", slug:"spotify"},
              {name:"Hulu", slug:"hulu"},
              {name:"Disney+", slug:"disney-plus"},
              {name:"Peacock", slug:"peacock"},
              {name:"Max", slug:"hbo-max"},
              {name:"Apple", slug:"apple-subs"},
              {name:"Planet Fitness", slug:"planet-fitness"},
            ].map((m) => (
              <Link key={m.slug} to={`/playbooks/${m.slug}`} data-testid={`landing-merchant-${m.slug}`}>
                <Card className="trust-card hover:border-[#93C5FD] transition-colors">
                  <CardContent className="p-6">
                    <p className="font-semibold text-slate-900">{m.name}</p>
                    <p className="text-xs text-slate-500 mt-1">Refund playbook</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Wall */}
      {wall && wall.claimed > 0 && (
        <section className="bg-white border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
            <div className="flex items-center gap-3 mb-3">
              <Users className="w-5 h-5 text-emerald-600" />
              <span className="text-xs font-semibold uppercase tracking-widest text-emerald-700">The Founding 100</span>
            </div>
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
                <span data-testid="founders-claimed-count">{wall.claimed}</span> of {wall.quota} founders claimed
              </h2>
              <p className="text-slate-600 max-w-md">
                {wall.remaining > 0
                  ? <>Only <strong data-testid="founders-remaining-count">{wall.remaining}</strong> free-first-case spots left. Grab yours before it's gone.</>
                  : "The Founding 100 is full — thanks to everyone who joined."}
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="founder-wall-avatars">
              {wall.founders.slice(0, 40).map((f) => (
                <div key={f.founding_number} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full pl-1 pr-3 py-1" title={`Founder #${f.founding_number}`}>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#0EA5E9] text-white text-xs font-bold grid place-items-center">
                    {initials(f.display_name).toUpperCase()}
                  </div>
                  <span className="text-xs font-medium text-slate-700">{f.display_name}</span>
                  <span className="text-[10px] text-slate-400">#{f.founding_number}</span>
                </div>
              ))}
              {wall.claimed > 40 && (
                <div className="flex items-center px-3 py-1 bg-slate-50 border border-slate-200 rounded-full text-xs text-slate-500">
                  + {wall.claimed - 40} more
                </div>
              )}
            </div>
            {wall.remaining > 0 && (
              <div className="mt-8">
                <Link to="/founding-100" data-testid="founder-wall-cta">
                  <Button size="lg" className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-12 px-6">
                    Claim your founder spot <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Ready to get your money back?</h2>
          <p className="text-slate-600 mt-3">$19 per case. No subscription. No auto-submissions.</p>
          <Link to="/auth" data-testid="footer-cta-button">
            <Button size="lg" className="mt-8 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white px-8 h-14 text-base font-semibold">
              Start my case <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
